import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { ShareRecordType } from "../../lib/types";
import { useEffect, useMemo, useState } from "react";

export interface SharedRecordItem {
  id: string;
  label: string;
  recordId?: string;
  recordType: ShareRecordType;
  permission: string;
}

interface SharedRecordsViewProps {
  records: SharedRecordItem[];
  onUnshare?: (shareId: string) => void;
  unsharingId?: string | null;
  showTitle?: boolean;
  groupByLabel?: boolean;
  showRecordType?: boolean;
  showPermission?: boolean;
}

const SharedRecordsView: React.FC<SharedRecordsViewProps> = ({
  records,
  onUnshare,
  unsharingId,
  showTitle = true,
  groupByLabel = false,
  showRecordType = true,
  showPermission = true,
}) => {
  const pageSize = 5;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(records.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [records.length]);

  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return records.slice(start, start + pageSize);
  }, [page, records]);

  const groupedRecords = useMemo(() => {
    if (!groupByLabel) {
      return null;
    }
    const groups = new Map<string, SharedRecordItem[]>();
    paginatedRecords.forEach((record) => {
      const key = String(record.label || "").trim() || "Untitled";
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)?.push(record);
    });
    return Array.from(groups.entries()).map(([groupLabel, items]) => ({
      groupLabel,
      items,
    }));
  }, [groupByLabel, paginatedRecords]);

  return (
    <Card elevation={0} sx={{ borderRadius: 1 }}>
      <CardContent>
        <Stack spacing={2}>
          {showTitle && (
            <Typography variant="h6" fontWeight={700}>
              Shared items
            </Typography>
          )}
          {records.length === 0 ? (
            <Typography color="text.secondary">
              No items shared with this group yet.
            </Typography>
          ) : (
            <>
              {groupByLabel ? (
                <Stack spacing={1.25}>
                  {(groupedRecords || []).map((group) => (
                    <Box
                      key={group.groupLabel}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      >
                        {group.groupLabel}
                      </Typography>
                      <List>
                        {group.items.map((record) => (
                          <ListItem key={record.id} divider>
                            <ListItemText
                              primary={record.label}
                              secondary={
                                showRecordType ? record.recordType : undefined
                              }
                            />
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              {showPermission ? (
                                <Chip label={record.permission} size="small" />
                              ) : null}
                              {onUnshare && (
                                <Tooltip title="Unshare">
                                  <span>
                                    <IconButton
                                      onClick={() => {
                                        console.log("Unsharing record", {
                                          shareId: record.id,
                                        });
                                        onUnshare(record.id);
                                      }}
                                      disabled={unsharingId === record.id}
                                    >
                                      {unsharingId === record.id ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <LinkOffIcon />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </Stack>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <List>
                  {paginatedRecords.map((record) => (
                    <ListItem key={record.id} divider>
                      <ListItemText
                        primary={record.label}
                        secondary={
                          showRecordType ? record.recordType : undefined
                        }
                      />
                      <Stack direction="row" spacing={1} alignItems="center">
                        {showPermission ? (
                          <Chip label={record.permission} size="small" />
                        ) : null}
                        {onUnshare && (
                          <Tooltip title="Unshare">
                            <span>
                              <IconButton
                                onClick={() => {
                                  console.log("Unsharing record", {
                                    shareId: record.id,
                                  });
                                  onUnshare(record.id);
                                }}
                                disabled={unsharingId === record.id}
                              >
                                {unsharingId === record.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <LinkOffIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </ListItem>
                  ))}
                </List>
              )}
              {records.length > pageSize ? (
                <Stack direction="row" justifyContent="center">
                  <Pagination
                    count={totalPages}
                    page={Math.min(page, totalPages)}
                    onChange={(_, nextPage) => setPage(nextPage)}
                    size="small"
                    color="primary"
                  />
                </Stack>
              ) : null}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SharedRecordsView;
